/**
 * Hook that owns the data-fetching lifecycle for the external single-order
 * layout and derives the patient / provider info-grid rows.
 *
 * Wraps {@link useDataAccumulatorForSingleOrder}, kicks off `fetchAll`
 * plus the audit-trail overview slice when `orderId` changes, and
 * memoizes the patient / provider grid data instead of mirroring it
 * into local state.
 *
 * @module singleOrderForExternal/hooks/useSingleOrderForExternal
 */
import { useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useDataAccumulatorForSingleOrder } from "../../../context/dataAccumulatorForSingleOrder";
import { InfoGridData } from "../../../data-model/infoGrid";
import { fetchAuditTrialOverview } from "../../../redux/slice/auditTrialOverviewSlice";
import { AppDispatch } from "../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../utils/organizationHelper";
import {
  generatePatientDetails,
  generateProviderDetails,
} from "../../worklists/singleOrder/utils/singleOrderPageDataGenerator";

/**
 * Fetches and exposes everything the external single-order layout needs
 * to render (order, coverage, auth-on-file, submission, plus derived grids).
 *
 * @param orderId - Route-scoped order id from `useParams`
 * @returns Loading flag, raw context slices, and memoized info-grid rows
 */
export const useSingleOrderForExternal = (orderId: string | undefined) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
    submissionData,
    loading,
    fetchAll,
  } = useDataAccumulatorForSingleOrder();

  useEffect(() => {
    if (orderId) {
      void fetchAll(orderId, getOrgIdForFetchExternalWorklist());
      dispatch(fetchAuditTrialOverview(orderId));
    }
    // `fetchAll` / `dispatch` are stable references; we intentionally
    // re-fetch only when the route order id changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  const patientData = useMemo<InfoGridData[] | null>(() => {
    if (!generalOrderDetails || !coverageDetails) return null;
    return generatePatientDetails(
      generalOrderDetails.order,
      coverageDetails?.coverages ?? [],
    ) as InfoGridData[];
  }, [generalOrderDetails, coverageDetails]);

  const providerData = useMemo<InfoGridData[] | null>(() => {
    if (!generalOrderDetails || !submissionData || !coverageDetails)
      return null;
    return generateProviderDetails(
      submissionData?.submission?.requesting_practitioner_details,
    ) as InfoGridData[];
  }, [generalOrderDetails, submissionData, coverageDetails]);

  return {
    loading,
    generalOrderDetails,
    authOnFileDetails,
    coverageDetails,
    submissionData,
    patientData,
    providerData,
  };
};
